"use client"

import { useState } from "react"
import Link from "next/link"
import { ShieldCheck, MessageSquare, Twitter, Youtube, Send } from "lucide-react"
import { DragonIcon } from "@/components/ui/DragonIcon"

export function Footer() {
    const [subscribed, setSubscribed] = useState(false)

    return (
        <footer className="relative bg-[#0B0908] pt-24 pb-12 overflow-hidden border-t border-[#3E3125]/50">
            {/* Top Border Gradient */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C88246]/40 to-transparent" />

            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-[#681812]/15 rounded-full blur-[100px] pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
                    {/* Brand Section */}
                    <div className="md:col-span-1 space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-gradient-to-br from-[#681812] to-[#3E0E0A] rounded-lg flex items-center justify-center shadow-lg shadow-[#681812]/20 border border-[#C88246]/40">
                                <DragonIcon className="text-[#D8B478] h-7 w-7" />
                            </div>
                            <span className="font-black text-2xl text-white tracking-tighter uppercase italic font-cinzel">
                                Dragon<span className="text-[#C88246]">L2</span>
                            </span>
                        </div>
                        <p className="text-[#9E8B78] text-sm leading-relaxed font-medium font-sans">
                            The ultimate Lineage 2 High Rate experience. Join a world of legend, conflict, and victory since 2012.
                        </p>
                        <div className="flex items-center gap-4">
                            <a href="https://discord.gg/QbwJJHY52A" target="_blank" rel="noopener noreferrer" className="p-2 bg-[#14110F] border border-[#3E3125] rounded-lg text-[#9E8B78] hover:text-[#D8B478] hover:border-[#C88246]/60 transition-all hover:shadow-lg hover:shadow-[#C88246]/10" title="Discord">
                                <MessageSquare className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    {/* Links - Game */}
                    <div>
                        <h4 className="text-[#D8B478] font-bold text-xs uppercase tracking-[0.2em] mb-8 font-cinzel">Adventure</h4>
                        <ul className="space-y-4">
                            <li><Link href="/register" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Start Now</Link></li>
                            <li><Link href="/download" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Download Client</Link></li>
                            <li><Link href="/ranking" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Hero Rankings</Link></li>
                            <li><Link href="/shop" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Premium Shop</Link></li>
                        </ul>
                    </div>

                    {/* Links - Support */}
                    <div>
                        <h4 className="text-[#D8B478] font-bold text-xs uppercase tracking-[0.2em] mb-8 font-cinzel">Support</h4>
                        <ul className="space-y-4">
                            <li><Link href="/faq" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Troubleshooting</Link></li>
                            <li><Link href="/rules" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Server Rules</Link></li>
                            <li><Link href="/security" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Account Protection</Link></li>
                            <li><Link href="/contact" className="text-[#9E8B78] hover:text-[#EDE3D4] text-sm font-bold transition-colors font-sans hover:pl-2">Contact Staff</Link></li>
                        </ul>
                    </div>

                    {/* Subscription Preview */}
                    <div className="p-8 bg-[#14110F]/80 backdrop-blur-sm rounded-xl border border-[#3E3125] hover:border-[#C88246]/40 transition-colors group">
                        <h4 className="text-white font-bold text-xs uppercase tracking-[0.2em] mb-4 font-cinzel group-hover:text-amber-400 transition-colors">Stay Notified</h4>
                        <p className="text-[#7E6A56] text-xs leading-relaxed mb-6 font-medium font-sans">
                            Get the latest updates, event schedules, and exclusive item drops.
                        </p>
                        {subscribed ? (
                            <p className="text-[#4CC38A] text-sm font-semibold text-center py-2 font-sans">Thanks! We&apos;ll keep you updated.</p>
                        ) : (
                            <form onSubmit={(e) => { e.preventDefault(); setSubscribed(true) }} className="relative">
                                <input
                                    type="email"
                                    required
                                    placeholder="Email address..."
                                    className="w-full bg-[#0B0908] border border-[#3E3125] rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C88246]/60 focus:bg-[#14110F] transition-all font-sans placeholder:text-[#5E4D3E]"
                                />
                                <button type="submit" className="absolute right-2 top-2 p-1.5 bg-[#681812]/70 border border-[#C88246]/50 rounded text-[#D8B478] hover:bg-[#681812] hover:text-white transition-all cursor-pointer">
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </form>
                        )}
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="pt-8 border-t border-[#241C16] flex flex-col md:flex-row items-center justify-between gap-6">
                    <p className="text-[#645343] text-[10px] font-bold uppercase tracking-widest font-sans">
                        © 2026 DragonL2 Project. Developed for Ultimate Competition.
                    </p>
                    <div className="flex items-center gap-8">
                        <Link href="/terms" className="text-[#645343] hover:text-[#D8B478] text-[10px] font-bold uppercase tracking-widest transition-colors font-sans">Terms of War</Link>
                        <Link href="/privacy" className="text-[#645343] hover:text-[#D8B478] text-[10px] font-bold uppercase tracking-widest transition-colors font-sans">Privacy Scrolls</Link>
                    </div>
                </div>
            </div>
        </footer>
    )
}

function ChevronRight(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m9 18 6-6-6-6" />
        </svg>
    )
}
